/**
 * 景品交換モーダル
 * 作成日: 2025-10-18
 * 機能: POSレジから独立した景品交換処理
 */

import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../../utils/apiClient';
import './PrizeExchangeModal.css';

interface PrizeExchangeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApply?: (prizeValue: any) => void;
}

interface PrizeInfo {
    couponId: number;
    shopItemId: string;
    prizeName: string;
    icon: string;
    description: string;
    userName: string;
    stock: number | null;
}

const PrizeExchangeModal: React.FC<PrizeExchangeModalProps> = ({ isOpen, onClose, onApply }) => {
    if (!isOpen) return null;
    const [barcodeInput, setBarcodeInput] = useState('');
    const [prizeInfo, setPrizeInfo] = useState<PrizeInfo | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // マウント時にフォーカス
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const normalizeBarcode = (s: string) => s
        .trim()
        .replace(/[Ａ-Ｚａ-ｚ０-９]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
        .replace(/[－ー]/g, '-')
        .toUpperCase();

    // バーコードスキャン処理
    const handleScan = async (barcode: string) => {
        const normalized = normalizeBarcode(barcode);
        if (!normalized) return;

        setLoading(true);
        setError('');

        try {
            const response = await apiClient.post('/shop/validate-prize', {
                barcodeData: normalized
            });
            const data = response?.data;
            const failed =
                data?.success === false ||
                !!data?.error ||
                (!!data?.message && !data?.prize) ||
                !data?.prize;

            if (failed) {
                const msg = data?.error || data?.message || '景品クーポンが見つかりません';
                setError(msg);
                return;
            }

            setError('');
            setPrizeInfo(data.prize);
        } catch (err: any) {
            setError(err.response?.data?.error || 'エラーが発生しました');
        } finally {
            setLoading(false);
        }
    };

    // Enterキーでスキャン
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleScan(barcodeInput);
        }
    };

    // 景品交換確定
    const confirmExchange = async () => {
        if (!prizeInfo) return;

        if (!confirm(`${prizeInfo.prizeName}を${prizeInfo.userName}様にお渡ししますか？`)) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await apiClient.post('/shop/confirm-prize-exchange', {
                couponId: prizeInfo.couponId,
                shopItemId: prizeInfo.shopItemId
            });
            const data = response?.data;

            if (data?.success === false || data?.error) {
                const msg = data?.error || data?.message || '使用済みの景品クーポンです';
                setError(msg);
                return;
            }

            if (data?.success) {
                alert('✅ 景品交換が完了しました');
                // リセット
                setBarcodeInput('');
                setPrizeInfo(null);
                inputRef.current?.focus();
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.response?.data?.message || '交換に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="prize-modal-overlay" onClick={onClose}>
            <div className="prize-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="prize-modal-header">
                    <h2>🎁 景品交換</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="prize-modal-body">
                    <div className="scan-section">
                        <label>景品バーコードをスキャン</label>
                        <input
                            ref={inputRef}
                            type="text"
                            className="barcode-input"
                            value={barcodeInput}
                            onChange={(e) => setBarcodeInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="バーコードを読み取ってください"
                            disabled={loading}
                        />
                        <button
                            className="scan-btn"
                            onClick={() => handleScan(barcodeInput)}
                            disabled={loading}>
                            {loading ? '確認中...' : '確認'}
                        </button>
                    </div>

                    {/* 数字・英字パッド */}
                    <div className="number-pad" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '16px' }}>
                        {['1', '2', '3', 'A', '4', '5', '6', 'B', '7', '8', '9', 'C', '0', 'D', 'E', 'F', '-'].map(key => (
                            <button
                                key={key}
                                onClick={() => setBarcodeInput(prev => prev + key)}
                                className="number-btn"
                                style={{
                                    padding: '12px',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    backgroundColor: '#f0f0f0',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                {key}
                            </button>
                        ))}
                        <button
                            onClick={() => setBarcodeInput('')}
                            className="clear-btn"
                            style={{
                                padding: '12px',
                                fontSize: '16px',
                                backgroundColor: '#ff6b6b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            クリア
                        </button>
                    </div>

                    {error && (
                        <div className="error-message">
                            ❌ {error}
                        </div>
                    )}

                    {prizeInfo && (
                        <div className="prize-info-display">
                            <div className="prize-icon-large">{prizeInfo.icon}</div>
                            <h3 className="prize-name">{prizeInfo.prizeName}</h3>
                            <p className="prize-description">{prizeInfo.description}</p>
                            {prizeInfo.userName && prizeInfo.userName !== 'ゲスト' && (
                                <div className="prize-user-info">
                                    <strong>お客様:</strong> {prizeInfo.userName}
                                </div>
                            )}
                            <button
                                className="confirm-exchange-btn"
                                onClick={confirmExchange}
                                disabled={loading}>
                                景品をお渡しして完了
                            </button>
                        </div>
                    )}
                </div>

                <div className="prize-modal-footer">
                    <p className="info-text">
                        💡 景品交換は何度でも可能です。会計前後どちらでもOKです。
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PrizeExchangeModal;
